import { findMockedNodes, findNode, getDefaultPaginationSettings, getDefaultTablePaginationConfig, handleResponse } from '@/Helper';
import { deviceAdminApi } from '@/api';
import { PaginationSetting } from '@/model/CommonModel';
import { HardwarePaginationSetting, ObjectLibraryFolder, ObjectTypes } from '@/model/DeviceAdminModel';
import { DirectoryTreeOnExpandParams, ObjectLibraryTreeNode, ObjectSearchField } from '@/model/GraphicModel';
import { Logger } from '@/model/LoggingModel';
import { ApplyAction, ApplyChanges, ConfirmChangesData, DigiTracPermission, HardwarePermission, SearchType } from '@/model/RolesModel';
import { Pagination, Spin } from 'antd';
import { CheckboxChangeEvent } from 'antd/es/checkbox';
import { TablePaginationConfig } from 'antd/lib';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { batch } from 'react-redux';
import { RoleStore } from '../../RoleContext/context';
import { isAllChildrenSelected, updateAllChildren, updateParentIsAllChildrenSelected } from '../../helper';
import { ConfirmChanges } from '../ConfirmChanges/ConfirmChanges';
import { HardwareView } from '../HardwarePermissions';
import { SearchHardware, defaultSearchType, isDeepChildren } from '../SearchHardware/SearchHardware';
import { HierarchyView } from './HierarchyView/HierarchyView';
import styles from './nodeTreeView.module.scss';

type Props = {
	availableHardwarePermissions: DigiTracPermission;
	hardwareView: Exclude<HardwareView, HardwareView.GroupView | HardwareView.ListView>;
	handleOnAppendPermissions: (extraPermissions: HardwarePermission[]) => void;
	handleOnModifiedPermissions: (modifiedPermissions: HardwarePermission[]) => void;
};

const defaultPagination: HardwarePaginationSetting = { ...getDefaultPaginationSettings(), ExcludePagination: false };

const NodeTreeView: React.FC<Props> = ({ hardwareView, availableHardwarePermissions, handleOnAppendPermissions, handleOnModifiedPermissions }) => {
	//#region state management variables
	const {
		roleContextInitialState: { forceRender },
	} = useContext(RoleStore);

	const [expandedKeys, setExpandedKeys] = useState<React.Key[]>([]);
	const [autoExpandParent, setAutoExpandParent] = useState<boolean>(true);
	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [fakeRender, setFakeRender] = useState<boolean>(true);
	const [searchType, setSearchType] = useState<SearchType>(defaultSearchType);
	const [pagination, setPagination] = useState<PaginationSetting>({
		PageNumber: defaultPagination.PageNumber,
		PageSize: defaultPagination.PageSize,
		SearchedValue: defaultPagination.SearchedValue,
		SortDirection: defaultSearchType.sortConfig.direction,
		SortField: defaultSearchType.sortConfig.field,
		TotalItems: defaultPagination.TotalItems,
	});
	const [confirmChangesData, setConfirmChangesData] = useState<ConfirmChangesData>({
		applyAction: ApplyAction.None,
		currentSelectedNode: undefined,
		isFolder: false,
	});
	const [isAnyFilterApplied, setIsAnyFilterApplied] = useState<boolean>(false);

	const objectDirectoryLibraryRef = useRef<ObjectLibraryTreeNode>(undefined);
	const setObjectDirectoryLibraryRef = (newObjectDirectoryLibraryRef: ObjectLibraryTreeNode): void => {
		objectDirectoryLibraryRef.current = newObjectDirectoryLibraryRef;
		setFakeRender(prevState => !prevState);
	};
	const getObjectDirectoryLibraryRef = (): ObjectLibraryTreeNode => {
		return objectDirectoryLibraryRef.current;
	};
	//#endregion

	useEffect(() => {
		fetchData(pagination, searchType, true);
	}, []);

	useEffect(() => {
		if (forceRender) {
			fetchData(pagination, searchType, false);
		}
	}, [forceRender]);

	const fetchData = async (pagination: PaginationSetting, searchType: SearchType, firstLoad: boolean = false) => {
		if (!isLoading) {
			setIsLoading(true);
		}

		try {
			const permissions = getCurrentAvailableHardwarePermissions();
			const response = await deviceAdminApi.getHardware(pagination, searchType, permissions, availableHardwarePermissions.ControllerDefinitions);
			if (!handleResponse(response)) {
				const {
					Entity: { HardwareTree, TotalItemsPaginated },
				} = response;
				const clonedPaginationState = {
					...pagination,
					TotalItems: TotalItemsPaginated,
				};
				setPagination(clonedPaginationState);
				reassignParentNode(availableHardwarePermissions.ControllerFullSelected, HardwareTree, undefined);
				if (!firstLoad) {
					const searchItems = searchType.isDeepChildren ? [] : findMockedNodes(HardwareTree, searchType?.selectedSearchByHardwareTypes);
					const rootNode = await replaceMockedChildren(searchItems, HardwareTree);
					isAllChildrenSelected(rootNode);
					setObjectDirectoryLibraryRef(rootNode);
					setExpandedNodes(rootNode, searchType);
				} else {
					isAllChildrenSelected(HardwareTree);
					setObjectDirectoryLibraryRef(HardwareTree);
				}
			}
		} catch (err) {
			Logger.writeErrorLog(`${err.name}: ${err.message}`);
		}

		setIsLoading(false);
	};

	const keywordFilter = (parentNode: ObjectLibraryTreeNode, searchType: SearchType) => {
		const keyword = searchType.searchText?.trim()?.toLowerCase() ?? '';
		let newNodes: React.Key[] = [];
		if (!keyword) {
			return expandedKeys;
		}

		for (const n of parentNode.children) {
			let childrenNodes: React.Key[] = [];
			if (n.children.length) {
				childrenNodes = keywordFilter(n, searchType);
			}

			if (
				childrenNodes.length ||
				(n.folderType === ObjectLibraryFolder.ITEM &&
					searchType.selectedSearchByHardwareTypes.some(x => x === n.extendedData.objectType) &&
					n.displayName &&
					n.displayName.toLowerCase().indexOf(keyword) > -1)
			) {
				childrenNodes.push(n.key);
			}
			newNodes = newNodes.concat(childrenNodes);
		}

		if (
			parentNode.folderType === ObjectLibraryFolder.ITEM &&
			searchType.selectedSearchByHardwareTypes.some(x => x === parentNode.extendedData.objectType) &&
			parentNode.displayName &&
			parentNode.displayName.toLowerCase().indexOf(keyword) > 0
		) {
			newNodes.push(parentNode.key);
		}

		return newNodes;
	};

	const getCurrentAvailableHardwarePermissions = (): HardwarePermission[] => {
		const retValue: HardwarePermission[] = Object.values(availableHardwarePermissions.Permissions)
			.map(x => x['Items'])
			.map(x => Object.values(x))
			.flat(1) as HardwarePermission[];

		return retValue ?? [];
	};

	const setExpandedNodes = (rootNode: ObjectLibraryTreeNode, searchType: SearchType) => {
		const searchedNodes: React.Key[] = keywordFilter(rootNode, searchType);
		batch(() => {
			setAutoExpandParent(searchedNodes.length > 0);
			setExpandedKeys(searchedNodes);
		});
	};

	const handleOnExpand = async (expandedKeys: React.Key[], params: DirectoryTreeOnExpandParams) => {
		const currentNode: ObjectLibraryTreeNode = params.node;

		const objectSearchFields: ObjectSearchField[] = [];
		if (currentNode.extendedData.hasMockedData) {
			objectSearchFields.push({
				ParentId: currentNode?.parentNode?.extendedData?.objectId,
				ObjectId: currentNode.extendedData.objectId,
				PortStyle: currentNode.extendedData.portStyle,
				ObjectType: currentNode.extendedData.objectType,
			});
		}
		currentNode?.children.forEach(child => {
			if (child.extendedData.hasMockedData) {
				objectSearchFields.push({
					ParentId: child?.parentNode?.extendedData?.objectId,
					ObjectId: child.extendedData.objectId,
					PortStyle: child.extendedData.portStyle,
					ObjectType: child.extendedData.objectType,
				});
			}
		});

		if (objectSearchFields.length) {
			try {
				setIsLoading(true);
				const response = await deviceAdminApi.searchHardware(objectSearchFields, getCurrentAvailableHardwarePermissions());
				if (handleResponse(response)) {
					return;
				}

				//We need to do a lookup since the params is not the object reference
				const rootNode = getObjectDirectoryLibraryRef();
				const extraPermissions: HardwarePermission[] = [];
				objectSearchFields.forEach(child => {
					const newNode = findNode(rootNode, {
						ObjectType: child.ObjectType,
						ParentId: child.ParentId,
						ObjectId: child.ObjectId,
						PortStyle: child.PortStyle,
					});
					newNode.extendedData.hasMockedData = false;
					newNode.children =
						response.Entity.find(
							x =>
								x.extendedData.objectId === child.ParentId &&
								x.extendedData.objectType === child.ObjectType &&
								x.extendedData.portStyle === child.PortStyle
						)?.children ?? [];
					newNode.children.forEach(x => {
						if (x.isChecked) {
							extraPermissions.push({
								ObjectId: x.extendedData.objectId,
								ParentId: x?.parentNode?.extendedData?.objectId,
								ObjectType: x.extendedData.objectType,
								HasPermission: true,
							});
						}
					});
					reassignParentNode(availableHardwarePermissions.ControllerFullSelected, newNode, newNode.parentNode);
					isAllChildrenSelected(newNode);
				});
				handleOnAppendPermissions(extraPermissions);
				setFakeRender(prevState => !prevState);
			} catch (err) {
				Logger.writeErrorLog(`${err.name}: ${err.message}`);
			}
		}

		batch(() => {
			setExpandedKeys(expandedKeys);
			setAutoExpandParent(false);
			setIsLoading(false);
		});
	};

	const reassignParentNode = (controllerFullSelected: HardwarePermission[], childNode: ObjectLibraryTreeNode, parentNode: ObjectLibraryTreeNode) => {
		const { children } = childNode;

		childNode.parentNode = parentNode;
		if (childNode.extendedData.objectType === ObjectTypes.hardwareControllers) {
			const isControllerFullSelected = controllerFullSelected?.find(item => item.ObjectId === childNode.extendedData.objectId)?.HasPermission;
			if (isControllerFullSelected) {
				for (let i = 0; i < children.length; i++) {
					updateAllChildren(children[i], true);
				}
			}
		}

		for (let i = 0; i < children.length; i++) {
			reassignParentNode(controllerFullSelected, children[i], childNode);
		}
	};

	const handleOnSearch = (searchType: SearchType, isFilterApplied: boolean) => {
		const clonedState: PaginationSetting = {
			...pagination,
			PageNumber: 1,
			SearchedValue: searchType.searchText,
			SortDirection: searchType.sortConfig.direction,
			SortField: searchType.sortConfig.field,
		};
		batch(() => {
			setSearchType(searchType);
			setIsAnyFilterApplied(isFilterApplied);
		});
		fetchData(clonedState, searchType);
	};

	const handleChangePagination = (page: number, pageSize: number) => {
		let clonedPagination: PaginationSetting = { ...pagination, PageNumber: page };
		if (pageSize !== pagination.PageSize) {
			clonedPagination.PageSize = pageSize;
		}
		fetchData(clonedPagination, searchType);
	};

	const replaceMockedChildren = async (searchItems: ObjectLibraryTreeNode[], rootNode: ObjectLibraryTreeNode): Promise<ObjectLibraryTreeNode> => {
		if (searchItems.length) {
			try {
				const response = await deviceAdminApi.searchHardware(
					searchItems.map(x => ({
						ObjectId: x.extendedData.objectId,
						ParentId: x.parentNode.extendedData.objectId,
						PortStyle: x.extendedData.portStyle,
						ObjectType: x.extendedData.objectType,
					})),
					getCurrentAvailableHardwarePermissions()
				);

				if (!handleResponse(response)) {
					const extraPermissions: HardwarePermission[] = [];
					searchItems.forEach(currentNode => {
						if (currentNode) {
							currentNode.extendedData.hasMockedData = false;
							currentNode.children =
								response.Entity.find(
									x =>
										x.extendedData.objectId === currentNode.parentNode.extendedData.objectId &&
										x.extendedData.objectType === currentNode.extendedData.objectType &&
										x.extendedData.portStyle === currentNode.extendedData.portStyle
								)?.children ?? [];
							currentNode.children.forEach(x => {
								if (x.isChecked) {
									extraPermissions.push({
										ObjectId: x.extendedData.objectId,
										ParentId: x?.parentNode?.extendedData?.objectId,
										ObjectType: x.extendedData.objectType,
										HasPermission: true,
									});
								}
							});
							reassignParentNode(availableHardwarePermissions.ControllerFullSelected, currentNode, currentNode.parentNode);
						}
					});
					handleOnAppendPermissions(extraPermissions);
				}
			} catch (err) {
				Logger.writeErrorLog(`${err.name}: ${err.message}`);
			}
		}

		return rootNode;
	};

	const rebuildTreeByFilters = (
		searchType: SearchType,
		nodes: ObjectLibraryTreeNode[],
		parent: ObjectLibraryTreeNode = undefined
	): ObjectLibraryTreeNode[] => {
		const { selectedHardwareOptions, isDeepChildren } = searchType;
		if (!selectedHardwareOptions || !nodes) {
			return nodes;
		}

		const returnNodes: ObjectLibraryTreeNode[] = [];
		nodes.forEach(node => {
			let retValue: ObjectLibraryTreeNode = undefined;
			const { objectType } = node.extendedData;

			if (selectedHardwareOptions.some(o => o === objectType) && !(isDeepChildren && node.folderType === ObjectLibraryFolder.FOLDER)) {
				retValue = { ...node, children: [], parentNode: undefined };
			} else {
				if (!isDeepChildren || (isDeepChildren && !node.extendedData.hasMockedData)) {
					returnNodes.push(...rebuildTreeByFilters(searchType, node.children, node));
				}
			}

			if (retValue) {
				retValue.children = rebuildTreeByFilters(searchType, node.children, retValue);
				retValue.parentNode = { ...parent, children: [] };
				if (
					node.folderType === ObjectLibraryFolder.FOLDER &&
					node.extendedData.objectType !== ObjectTypes.servers &&
					!searchType.selectedHardwareOptions.some(p => p === ObjectTypes.hardwareControllers)
				) {
					retValue.displayName = `${node.parentNode.displayName} / ${node.displayName}`;
				}
				returnNodes.push(retValue);
			}
		});

		return returnNodes;
	};

	const handleOnChangeDeviceCheckbox = async (event: CheckboxChangeEvent, node: ObjectLibraryTreeNode) => {
		const { checked } = event.target;
		if (!isDeepChildren(node.extendedData.objectType, node)) {
			setConfirmChangesData({
				applyAction: checked ? ApplyAction.Grant : ApplyAction.Revoke,
				currentSelectedNode: {
					ObjectType: node.extendedData.objectType,
					ParentId: node.parentNode.extendedData.objectId,
					ObjectId: node.extendedData.objectId,
					PortStyle: node.extendedData.portStyle,
				},
				isFolder: node.folderType === ObjectLibraryFolder.FOLDER,
			});
		}
		// this for folder type logic and deep children (doors, inputs, exp, etc)
		else {
			//We need to do a lookup since the params is not the object reference
			const rootNode = getObjectDirectoryLibraryRef();
			const newNode = findNode(rootNode, {
				ObjectType: node.extendedData.objectType,
				ParentId: node.parentNode.extendedData.objectId,
				ObjectId: node.extendedData.objectId,
				PortStyle: node.extendedData.portStyle,
			});
			if (newNode.extendedData.hasMockedData) {
				setIsLoading(true);
				await replaceMockedChildren([newNode], rootNode);
				const modifiedPermission = updateAllChildren(newNode, checked);
				isAllChildrenSelected(newNode);
				updateParentIsAllChildrenSelected(newNode?.parentNode);
				handleOnModifiedPermissions(modifiedPermission);
				setFakeRender(prevState => !prevState);
				setIsLoading(false);
			} else {
				const modifiedPermission = updateAllChildren(newNode, checked);
				isAllChildrenSelected(newNode);
				updateParentIsAllChildrenSelected(newNode?.parentNode);
				handleOnModifiedPermissions(modifiedPermission);
				setFakeRender(prevState => !prevState);
			}
		}
	};

	const handleOnHideConfirmModal = () => {
		setConfirmChangesData({
			applyAction: ApplyAction.None,
			currentSelectedNode: undefined,
			isFolder: false,
		});
	};

	const handleOnApplyChanges = async (checked: boolean, applyChanges: ApplyChanges) => {
		const rootNode = getObjectDirectoryLibraryRef();
		const node = findNode(rootNode, confirmChangesData.currentSelectedNode);
		const currentModified: HardwarePermission = {
			ObjectId: node.extendedData.objectId,
			ObjectType: node.extendedData.objectType,
			ParentId: node?.parentNode?.extendedData?.objectId,
			HasPermission: checked,
		};
		node.isChecked = checked;

		if (applyChanges === ApplyChanges.ToThisItem) {
			updateParentIsAllChildrenSelected(node?.parentNode);
			handleOnModifiedPermissions([currentModified]);
			handleOnHideConfirmModal();
		} else {
			let searchItems: ObjectLibraryTreeNode[];
			let selectedSearchByHardwareTypes: ObjectTypes[] = [];
			if (applyChanges === ApplyChanges.ToThisItemAndVisibleChildren) {
				selectedSearchByHardwareTypes = searchType?.selectedSearchByHardwareTypes;
			}
			searchItems = findMockedNodes(node, selectedSearchByHardwareTypes);
			setIsLoading(true);
			await replaceMockedChildren(searchItems, rootNode);
			const modifiedPermission = updateAllChildren(node, checked, selectedSearchByHardwareTypes);
			modifiedPermission.push(currentModified);
			isAllChildrenSelected(node);
			updateParentIsAllChildrenSelected(node?.parentNode);
			handleOnModifiedPermissions(modifiedPermission);
			handleOnHideConfirmModal();
			setIsLoading(false);
		}
	};

	const paginationConfig: TablePaginationConfig = getDefaultTablePaginationConfig(
		false,
		handleChangePagination,
		pagination.PageNumber,
		pagination.PageSize,
		pagination.TotalItems
	);

	let content: React.ReactNode = <></>;
	switch (hardwareView) {
		case HardwareView.CardView:
			break;

		case HardwareView.HierarchyView:
			content = (
				<HierarchyView
					data={getObjectDirectoryLibraryRef()}
					searchType={searchType}
					expandedKeys={expandedKeys}
					autoExpandParent={autoExpandParent}
					isAnyFilterApplied={isAnyFilterApplied}
					rebuildTreeByFilters={rebuildTreeByFilters}
					handleOnExpand={handleOnExpand}
					handleOnChangeDeviceCheckbox={handleOnChangeDeviceCheckbox}
				/>
			);
			break;
	}

	return (
		<>
			<Spin wrapperClassName={styles.spin} size='large' tip={`${_('Loading')}...`} spinning={isLoading}>
				<div className={styles.hardwareContainer}>
					<SearchHardware onSearchApplied={handleOnSearch} />
					{content}
					<div className={styles.paginationContainer}>
						<Pagination {...paginationConfig} />
					</div>
				</div>
			</Spin>
			{confirmChangesData.applyAction !== ApplyAction.None && (
				<ConfirmChanges data={confirmChangesData} handleOnHideModal={handleOnHideConfirmModal} handleOnApplyChanges={handleOnApplyChanges} />
			)}
		</>
	);
};

export { NodeTreeView };
